import { MdKeyboardArrowDown } from 'react-icons/md';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type SelectProps = Omit<ComponentPropsWithRef<'select'>, 'size'> & {
  invalid?: boolean;
  /** Icône décorative à gauche, alignée sur celle des `Input`. */
  icon?: ReactNode;
  containerClassName?: string;
};

/**
 * `<select>` natif habillé comme un `Input` : il occupe toute la boîte,
 * donc un clic n'importe où dans le cadre ouvre la liste.
 */
export function Select({
  className,
  containerClassName,
  invalid,
  icon,
  disabled,
  children,
  ...props
}: SelectProps) {
  return (
    <div
      className={cn(
        'relative flex h-[53px] w-full items-center rounded-field border bg-surface transition-colors',
        'border-border-input/50 focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600/15',
        invalid && 'border-danger focus-within:border-danger focus-within:ring-danger/15',
        disabled && 'cursor-not-allowed bg-surface-muted opacity-60',
        containerClassName,
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-3 flex text-content-muted',
            invalid && 'text-danger',
          )}
        >
          {icon}
        </span>
      ) : null}

      <select
        aria-invalid={invalid || undefined}
        disabled={disabled}
        className={cn(
          'peer h-full w-full min-w-0 appearance-none rounded-field bg-transparent pr-10 text-sm font-medium text-content outline-none',
          'disabled:cursor-not-allowed',
          icon ? 'pl-12' : 'pl-3',
          className,
        )}
        {...props}
      >
        {children}
      </select>

      <MdKeyboardArrowDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 size-5 text-content-subtle peer-focus:text-brand-600"
      />
    </div>
  );
}
